import { Controller, useForm } from 'react-hook-form';
import { ApolloCache } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Checkbox, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  EstateResultRule,
  EstateResultRule_GetAllEstateResultRulesDocument,
  EstateResultRule_GetAllEstateResultRulesQuery,
  EstateResultRule_UpdateEstateResultRuleInput,
  EstateResultRuleComparisonType,
  EstateResultRuleType,
  useEstateResultRule_CreateEstateResultRuleMutation,
  useEstateResultRule_DeleteEstateResultRuleMutation,
  useEstateResultRule_UpdateEstateResultRuleMutation,
} from 'queries';
import * as yup from 'yup';

import FormSelectField from 'common/form-select-field';
import FormTextField from 'common/form-text-field';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

type Props = {
  estateResultRule?: EstateResultRule;
  // eslint-disable-next-line @typescript-eslint/ban-types
  onExitClick: Function;
};

const useStyles = makeStyles(() => ({
  h5: {
    paddingTop: '5px',
  },
  formSection: {
    paddingBottom: '10px',
  },
  form: {
    textAlign: 'left',
    '& input': {
      paddingRight: '10px',
      padding: '1vh 2vh',
    },
  },
  inputGrid: {
    paddingRight: '20px',
  },
  inputError: {
    backgroundColor: '#FFF1F1',
    color: 'red',
  },
  container: {
    paddingTop: '10px',
  },
}));

// set up required fields and validation
const requiredTextFields = ['description'];
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const requiredTextFieldValidation = requiredTextFields.reduce((acc: any, val: string) => {
  acc[val] = yup.string().required('Required');
  return acc;
}, {});
const schema = yup.object().shape({
  ...requiredTextFieldValidation,
});

type EstateResultRuleFormValues = Pick<
  EstateResultRule_UpdateEstateResultRuleInput,
  | 'description'
  | 'type'
  | 'headerComparisonType'
  | 'headerComparisonValue'
  | 'subheaderComparisonType'
  | 'subheaderComparisonValue'
  | 'isActive'
>;

export default function EstateResultRuleForm({ estateResultRule, onExitClick }: Props) {
  const classes = useStyles();

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
  } = useForm<EstateResultRuleFormValues>({
    resolver: yupResolver(schema),
    values: {
      headerComparisonValue: estateResultRule?.headerComparisonType || '',
      subheaderComparisonValue: estateResultRule?.subheaderComparisonValue || '',
      headerComparisonType:
        estateResultRule?.headerComparisonType || EstateResultRuleComparisonType.None,
      subheaderComparisonType:
        estateResultRule?.subheaderComparisonType || EstateResultRuleComparisonType.None,
      description: estateResultRule?.description || '',
      type: estateResultRule?.type || EstateResultRuleType.SuspectedAccount,
      isActive: estateResultRule?.isActive || false,
    },
  });

  const readEstateResultRulesQuery = (
    store: ApolloCache<EstateResultRule_GetAllEstateResultRulesQuery>,
  ): EstateResultRule_GetAllEstateResultRulesQuery | null => {
    return store.readQuery<EstateResultRule_GetAllEstateResultRulesQuery>({
      query: EstateResultRule_GetAllEstateResultRulesDocument,
    });
  };

  const writeEstateResultRulesQuery = (
    store: ApolloCache<EstateResultRule_GetAllEstateResultRulesQuery>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rules: any,
  ) => {
    store.writeQuery<EstateResultRule_GetAllEstateResultRulesQuery>({
      query: EstateResultRule_GetAllEstateResultRulesDocument,
      data: {
        data: rules,
      },
    });
  };

  const [createEstateResultRule, { loading: createEstateResultRuleExecuting }] =
    useEstateResultRule_CreateEstateResultRuleMutation({
      update: (store, { data }) => {
        const ruleData = readEstateResultRulesQuery(store);
        if (ruleData) {
          const rules = [...ruleData.data, data!.data];
          writeEstateResultRulesQuery(store, rules);
        }
        onExitClick();
      },
      onError: (e) =>
        showAlert({
          message: `Error creating estate result rule: ${e.message}`,
          severity: NotificationSeverity.ERROR,
        }),
    });

  const [updateEstateResultRule, { loading: updateEstateResultRuleExecuting }] =
    useEstateResultRule_UpdateEstateResultRuleMutation({
      update: (store) => {
        const ruleData = readEstateResultRulesQuery(store);
        if (ruleData) {
          writeEstateResultRulesQuery(store, ruleData.data);
        }
        onExitClick();
      },
      onError: (e) =>
        showAlert({
          message: `Error updating estate result rule: ${e.message}`,
          severity: NotificationSeverity.ERROR,
        }),
    });

  const [deleteEstateResultRule, { loading: deleteEstateResultRuleExecuting }] =
    useEstateResultRule_DeleteEstateResultRuleMutation({
      update: (store, { data }) => {
        const ruleData = readEstateResultRulesQuery(store);
        if (ruleData) {
          const deletedRuleId = data!.data;
          const rules = [...ruleData.data.filter((r) => r.id !== deletedRuleId)];
          writeEstateResultRulesQuery(store, rules);
        }
        onExitClick();
      },
      onError: (e) =>
        showAlert({
          message: `Error deleting estate rule: ${e.message}`,
          severity: NotificationSeverity.ERROR,
        }),
    });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const submitForm = async (formData: any) => {
    if (estateResultRule?.id) {
      // updating existing estate result rule
      updateEstateResultRule({
        variables: {
          input: {
            ...formData,
            id: estateResultRule.id,
          },
        },
      });
    } else {
      // creating a new estate result rule
      createEstateResultRule({
        variables: {
          input: {
            ...formData,
          },
        },
      });
    }
  };

  const handleDeleteEstateResultRule = async () => {
    if (!window.confirm('Are you sure you want to delete this rule?')) {
      return;
    }

    deleteEstateResultRule({
      variables: {
        id: estateResultRule!.id!,
      },
    });
  };

  return (
    <>
      <Typography variant="h5" className={classes.h5}>
        Estate Result Rule Details
      </Typography>
      <form
        id="estate-result-rule-form"
        className={classes.form}
        onSubmit={handleSubmit(submitForm)}
      >
        <div className={classes.formSection}>
          <Grid container className={classes.container}>
            <Grid className={classes.inputGrid} item xs={12} sm={6}>
              <Controller
                name="description"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormTextField
                    {...field}
                    inputRef={ref}
                    errorMessage={errors.description?.message}
                    label="Description"
                    inputProps={{ maxLength: 80 }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="type"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormSelectField
                    {...field}
                    inputRef={ref}
                    options={[
                      {
                        label: 'Suspected Account',
                        value: EstateResultRuleType.SuspectedAccount,
                      },
                    ]}
                    label="Type"
                    errorMessage={errors.type?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container className={classes.container}>
            <Grid className={classes.inputGrid} item xs={12} sm={6}>
              <Controller
                name="headerComparisonType"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormSelectField
                    {...field}
                    inputRef={ref}
                    options={[
                      { label: 'None', value: EstateResultRuleComparisonType.None },
                      {
                        label: 'Contains',
                        value: EstateResultRuleComparisonType.Contains,
                      },
                      {
                        label: 'Equals',
                        value: EstateResultRuleComparisonType.Equals,
                      },
                      {
                        label: 'Starts With',
                        value: EstateResultRuleComparisonType.StartsWith,
                      },
                      {
                        label: 'Ends With',
                        value: EstateResultRuleComparisonType.EndsWith,
                      },
                      {
                        label: 'Not Contains',
                        value: EstateResultRuleComparisonType.NotContains,
                      },
                      {
                        label: 'Not Equals',
                        value: EstateResultRuleComparisonType.NotEquals,
                      },
                      {
                        label: 'Not Starts With',
                        value: EstateResultRuleComparisonType.NotStartsWith,
                      },
                      {
                        label: 'Not Ends With',
                        value: EstateResultRuleComparisonType.NotEndsWith,
                      },
                    ]}
                    label="Header Comparison Type"
                    errorMessage={errors.headerComparisonType?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="headerComparisonValue"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormTextField
                    {...field}
                    inputRef={ref}
                    errorMessage={errors.headerComparisonValue?.message}
                    label="Header Comparison Value"
                    inputProps={{ maxLength: 80 }}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container className={classes.container}>
            <Grid className={classes.inputGrid} item xs={12} sm={6}>
              <Controller
                name="subheaderComparisonType"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormSelectField
                    {...field}
                    inputRef={ref}
                    options={[
                      {
                        label: 'None',
                        value: EstateResultRuleComparisonType.None,
                      },
                      {
                        label: 'Contains',
                        value: EstateResultRuleComparisonType.Contains,
                      },
                      {
                        label: 'Equals',
                        value: EstateResultRuleComparisonType.Equals,
                      },
                      {
                        label: 'Starts With',
                        value: EstateResultRuleComparisonType.StartsWith,
                      },
                      {
                        label: 'Ends With',
                        value: EstateResultRuleComparisonType.EndsWith,
                      },
                      {
                        label: 'Not Contains',
                        value: EstateResultRuleComparisonType.NotContains,
                      },
                      {
                        label: 'Not Equals',
                        value: EstateResultRuleComparisonType.NotEquals,
                      },
                      {
                        label: 'Not Starts With',
                        value: EstateResultRuleComparisonType.NotStartsWith,
                      },
                      {
                        label: 'Not Ends With',
                        value: EstateResultRuleComparisonType.NotEndsWith,
                      },
                    ]}
                    label="Subheader Comparison Type"
                    errorMessage={errors.subheaderComparisonType?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="subheaderComparisonValue"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormTextField
                    {...field}
                    inputRef={ref}
                    errorMessage={errors.subheaderComparisonValue?.message}
                    label="Subheader Comparison Value"
                    inputProps={{ maxLength: 80 }}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container className={classes.container}>
            <Grid item xs={1} sm={1}>
              <Typography variant="caption">Is Active</Typography>
              <Controller
                name="isActive"
                control={control}
                render={({ field: { ref, onChange, ...field } }) => (
                  <Checkbox
                    {...field}
                    inputRef={ref}
                    color="primary"
                    checked={field.value || false}
                    onChange={(evt) => onChange(evt.target.checked)}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container className={classes.container}>
            <Grid className={classes.inputGrid} item xs={12} sm={6}>
              <Button
                type="submit"
                form="estate-result-rule-form"
                color="secondary"
                variant="contained"
                disabled={
                  isSubmitting ||
                  updateEstateResultRuleExecuting ||
                  createEstateResultRuleExecuting ||
                  deleteEstateResultRuleExecuting
                }
              >
                Save
              </Button>
            </Grid>
            {estateResultRule && estateResultRule.id && (
              <Grid className={classes.inputGrid} item xs={12} sm={6}>
                <Button variant="contained" onClick={handleDeleteEstateResultRule}>
                  Delete
                </Button>
              </Grid>
            )}
          </Grid>
        </div>
      </form>
    </>
  );
}
